// features/workstations/dashboard-sections/strategic-dialogue.js
// ═══════════════════════════════════════════════════════════
// STRATEGIC DIALOGUE — persistent chat with workstation context
// ═══════════════════════════════════════════════════════════
// Tier A Jarvis Surface. The conversational peer view — not
// a briefing, not a form. A running thread where the user can
// reason through positions, stress-test ideas, ask follow-ups.
// Every turn injects the full 5-layer context blob server-side.
//
// V1 scope: single persistent thread per workstation, plain
// back-and-forth, no streaming. Advanced features (multi-thread,
// starred messages, exports) deferred.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  PractitionerLens,
} from '../../../components/widgets/primitives';

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function StrategicDialogueSection({ workstation, userId }) {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [input, setInput] = useState('');

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
      });
      const res = await fetch(`/api/workstations/strategic-dialogue?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setThread(payload?.thread || null);
      setMessages(Array.isArray(payload?.messages) ? payload.messages : []);
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { load(); }, [load]);

  // Auto-scroll to bottom when messages change
  useLayoutEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, sending]);

  const send = useCallback(async () => {
    const content = input.trim();
    if (!content || sending) return;
    setSending(true);
    setError(null);
    // Optimistic user message render
    const pendingUser = {
      id: `pending-${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages((prev) => [...prev, pendingUser]);
    setInput('');

    try {
      const res = await fetch('/api/workstations/strategic-dialogue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstation.id,
          user_id: userId,
          content,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        // Keep the pending user message visible so they don’t
        // lose what they typed; mark it failed.
        setMessages((prev) => prev.map(m =>
          m.id === pendingUser.id ? { ...m, _failed: true } : m
        ));
        // If AI call failed but user message saved, still show it
        if (data?.user_message) {
          setMessages((prev) => prev.map(m =>
            m.id === pendingUser.id ? { ...data.user_message } : m
          ));
        }
        return;
      }
      // Replace pending user with persisted + append assistant
      setMessages((prev) => {
        const withoutPending = prev.filter(m => m.id !== pendingUser.id);
        const out = [...withoutPending];
        if (data.user_message) out.push(data.user_message);
        if (data.assistant_message) out.push(data.assistant_message);
        return out;
      });
    } catch (e) {
      setError(e?.message || 'Send failed');
      setMessages((prev) => prev.map(m =>
        m.id === pendingUser.id ? { ...m, _failed: true } : m
      ));
    } finally {
      setSending(false);
      // Return focus to input for rapid back-and-forth
      if (inputRef.current) inputRef.current.focus();
    }
  }, [input, sending, workstation?.id, userId]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <section className="ws-dialogue">
      <Eyebrow parts={[
        'strategic dialogue',
        thread?.message_count ? `${thread.message_count} turns` : 'new thread',
        workstation?.ai_name || null,
      ]} />

      {loading && <div className="ws-dialogue-loading">Loading thread…</div>}

      {!loading && messages.length === 0 && (
        <EmptyState ws={workstation} />
      )}

      {!loading && messages.length > 0 && (
        <div className="ws-dialogue-scroll" ref={scrollRef}>
          {messages.map((m) => (
            <MessageRow key={m.id} m={m} aiName={workstation?.ai_name} />
          ))}
          {sending && <TypingIndicator aiName={workstation?.ai_name} />}
        </div>
      )}

      {error && <div className="ws-dialogue-error">{error}</div>}

      <div className="ws-dialogue-input-row">
        <textarea
          ref={inputRef}
          className="ws-dialogue-input"
          rows={2}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask. Think out loud. Stress-test an idea. Enter to send, shift+enter for newline."
          disabled={sending}
        />
        <button
          type="button"
          className="ws-dialogue-send"
          onClick={send}
          disabled={!input.trim() || sending}
        >
          {sending ? '…' : 'send'}
        </button>
      </div>

      {messages.length === 0 && (
        <PractitionerLens label="what lives in this thread">
          Full workstation context is injected on every turn: your
          criteria, active theses, pattern library, recent trades,
          today’s force snapshot, open tensions. You don’t need to
          re-explain the setup — it’s already here.
        </PractitionerLens>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// MESSAGE ROW
// ═══════════════════════════════════════════════════════════
function MessageRow({ m, aiName }) {
  const isUser = m.role === 'user';
  const failed = m._failed;
  const pending = m._pending && !failed;
  const whoLabel = isUser ? 'you' : (aiName ? aiName.toLowerCase() : 'reply');

  return (
    <div className={`ws-dialogue-msg ws-dialogue-msg-${isUser ? 'user' : 'ai'}${failed ? ' is-failed' : ''}${pending ? ' is-pending' : ''}`}>
      <div className="ws-dialogue-msg-meta">
        <span className="ws-dialogue-msg-who">{whoLabel}</span>
        <span className="ws-dialogue-msg-time">{fmtTime(m.created_at)}</span>
        {failed && <span className="ws-dialogue-msg-failed">failed</span>}
        {pending && <span className="ws-dialogue-msg-pending">sending…</span>}
      </div>
      <div className="ws-dialogue-msg-body">{m.content}</div>
    </div>
  );
}

function TypingIndicator({ aiName }) {
  const whoLabel = aiName ? aiName.toLowerCase() : 'reply';
  return (
    <div className="ws-dialogue-msg ws-dialogue-msg-ai ws-dialogue-msg-typing">
      <div className="ws-dialogue-msg-meta">
        <span className="ws-dialogue-msg-who">{whoLabel}</span>
        <span className="ws-dialogue-msg-time">thinking…</span>
      </div>
      <div className="ws-dialogue-typing-dots">
        <span /> <span /> <span />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// EMPTY STATE
// ═══════════════════════════════════════════════════════════
function EmptyState({ ws }) {
  const aiName = ws?.ai_name || 'your strategic partner';
  return (
    <div className="ws-dialogue-empty">
      <SerifHeadline>Thread’s empty. Start anywhere.</SerifHeadline>
      <SerifSummary>
        Ask {aiName} to stress-test your current thesis, read the setup
        on a specific ticker against your criteria, or just think out
        loud about the session. Every response pulls your full workstation
        context — your rules, your patterns, your recent trades.
      </SerifSummary>
    </div>
  );
}

// ─── Helpers ───
function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diffMs = now - d.getTime();
    if (diffMs < 60000) return 'just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
